import classnames from 'classnames';
import React, {useState} from 'react';

import {Icon} from '@albert-io/atomic';
import type {IconProp, IconStyle} from '@albert-io/atomic/atoms/Icon/Icon.react';

const CLASSWORK_ROW_REDESIGN_CLASS_NAME = classnames(
  'gap-x2 p-x1 border-b-border-subtle flex items-center border border-transparent',
  'hover:shadow-raised hover:rounded-x2 hover:border-border-subtle',
  'has-focus-visible:shadow-raised has-focus-visible:rounded-x2',
  'has-focus-visible:border-border-subtle',
  '[@media(hover:hover)]:[li:has(+li:hover)>&]:border-b-transparent',
  '[li:has(+li_:focus-visible)>&]:border-b-transparent'
);

export const classworkListClassName = (isRowRedesignEnabled: boolean): string =>
  isRowRedesignEnabled
    ? 'm-x0 p-x0 gap-x0-half flex list-none flex-col'
    : 'm-x0 p-x0 flex list-none flex-col';

type Props = (
  | {
      icon: IconProp;
      /** Required: `Icon` defaults to solid, and most glyphs here are registered in one style only. */
      iconStyle: IconStyle;
      badge?: never;
    }
  | {badge: React.ReactElement; icon?: never; iconStyle?: never}
) & {
  title: React.ReactNode;
  /**
   * Rendered under the title. Receives whether the row is hovered or focused, so rows that reveal an
   * exact date on hover can swap their copy.
   */
  renderSubtitle: (isHovered: boolean) => React.ReactNode;
  /** Trailing actions. Their clicks are kept from also opening the row. */
  actions: React.ReactNode;
  onOpen: () => void;
  isRowRedesignEnabled?: boolean;
  'data-testid'?: string;
};

export const ClassworkRow = ({
  icon,
  iconStyle,
  badge,
  title,
  renderSubtitle,
  actions,
  onOpen,
  isRowRedesignEnabled = false,
  'data-testid': testId
}: Props) => {
  const [isHovered, setIsHovered] = useState(false);

  const hoverProps = {
    onMouseEnter: () => setIsHovered(true),
    onMouseLeave: () => setIsHovered(false),
    onFocus: () => setIsHovered(true),
    onBlur: () => setIsHovered(false)
  };

  const content = (
    <>
      {icon !== undefined ? (
        <span className='h-x5 w-x5 background-special-subtle content-special flex shrink-0 items-center justify-center rounded-full'>
          <Icon color='inherit' icon={icon} iconStyle={iconStyle} />
        </span>
      ) : (
        badge
      )}
      <div className='min-w-x0 flex grow flex-col'>
        {title}
        {renderSubtitle(isHovered)}
      </div>
    </>
  );

  if (isRowRedesignEnabled) {
    return (
      <div className={CLASSWORK_ROW_REDESIGN_CLASS_NAME} data-testid={testId} {...hoverProps}>
        {content}
        <div className='gap-x0-quarter flex shrink-0 items-center'>{actions}</div>
      </div>
    );
  }

  return (
    // Clicking the row opens it; the trailing buttons stop propagation so they act on their own.
    // Mouse-only convenience on top of the keyboard-accessible action buttons.
    // oxlint-disable-next-line jsx-a11y/click-events-have-key-events
    <div
      className='gap-x2 rounded-x1 p-x1 hover:background-primary-hover active:background-primary-pressed flex cursor-pointer items-center'
      data-testid={testId}
      onClick={onOpen}
      {...hoverProps}
    >
      {content}
      {/* oxlint-disable-next-line jsx-a11y/click-events-have-key-events */}
      <div className='gap-x0-half flex shrink-0 items-center' onClick={(e) => e.stopPropagation()}>
        {actions}
      </div>
    </div>
  );
};
